import { AfterViewInit, Component, Input, inject } from "@angular/core";
import {
  InlineEditor,
  ClassicEditor,
  AccessibilityHelp,
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  Code,
  Essentials,
  FullPage,
  GeneralHtmlSupport,
  Heading,
  HorizontalLine,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  ShowBlocks,
  FileRepository,
  SourceEditing,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Undo,
  EditorConfig,
} from "ckeditor5";

import { CkUploadAdapter } from "src/app/utils/ckeditor-uploader/ckeditor-uploader";

import { DatabaseService } from "src/app/servicios/database/database.service";
import { HttpClient } from "@angular/common/http";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";


@Component({
  selector: "app-html-editor",
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CKEditorModule
],
  templateUrl: "./html-editor.component.html",
  styleUrl: "./html-editor.component.scss",
})
export class HtmlEditorComponent implements AfterViewInit {
  private databaseService = inject(DatabaseService);
  private http = inject(HttpClient);

  public isLayoutReady: boolean = false;
  public editorConfig: EditorConfig;
  public editor;

  /** Formcontrol del editor para desactivar o establecer datos */
  @Input() formControlEditing = new FormControl();

  /** Datos a mostrar en el editor si no se necesita formcontrol */
  @Input() data: string;

  /** Tipo de editor Inline true (Oculta los controles por defecto), false o null (Muestra los controles siempre)  */
  @Input() inline: boolean;

  ngAfterViewInit(): void {
    this.editor = this.inline ? InlineEditor : ClassicEditor;
    this.formControlEditing.setValue(this.data);
    this.configureEditor();
  }

  /**
   * Inicializa el editor CK y le añade el uploader perzonalizado
   * @param editor Instancia del editor CK
   */
  onEditorReady(editor): void {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
      return new CkUploadAdapter(loader, this.http, this.databaseService);
    };
  }

  /**
   * Configura la herramientas e inicializa el editor
   */
  configureEditor() {
    this.editorConfig = {
      licenseKey: "GPL",
      language: {
        ui: 'es',
        content: 'es'
      },
      toolbar: {
        items: [
          "undo",
          "redo",
          "|",
          "sourceEditing",
          "showBlocks",
          "|",
          "heading",
          "|",
          "bold",
          "italic",
          "code",
          "|",
          "horizontalLine",
          "link",
          "insertImage",
          "insertTable",
          "blockQuote",
          "|",
          "alignment",
          "|",
          "bulletedList",
          "numberedList",
          "outdent",
          "indent",
        ],
        shouldNotGroupWhenFull: true,
      },
      plugins: [
        AccessibilityHelp,
        Alignment,
        Autoformat,
        AutoImage,
        AutoLink,
        Autosave,
        BlockQuote,
        Bold,
        Code,
        Essentials,
        FullPage,
        GeneralHtmlSupport,
        Heading,
        HorizontalLine,
        ImageBlock,
        ImageCaption,
        ImageInline,
        ImageInsert,
        ImageInsertViaUrl,
        ImageResize,
        ImageStyle,
        ImageTextAlternative,
        ImageToolbar,
        ImageUpload,
        Indent,
        IndentBlock,
        Italic,
        Link,
        LinkImage,
        List,
        ListProperties,
        Paragraph,
        PasteFromOffice,
        SelectAll,
        ShowBlocks,
        FileRepository,
        SourceEditing,
        Table,
        TableCaption,
        TableCellProperties,
        TableColumnResize,
        TableProperties,
        TableToolbar,
        TextTransformation,
        Undo,
      ],
      heading: {
        options: [
          {
            model: "paragraph",
            title: "Paragraph",
            class: "ck-heading_paragraph",
          },
          {
            model: "heading1",
            view: "h1",
            title: "Heading 1",
            class: "ck-heading_heading1",
          },
          {
            model: "heading2",
            view: "h2",
            title: "Heading 2",
            class: "ck-heading_heading2",
          },
          {
            model: "heading3",
            view: "h3",
            title: "Heading 3",
            class: "ck-heading_heading3",
          },
          {
            model: "heading4",
            view: "h4",
            title: "Heading 4",
            class: "ck-heading_heading4",
          },
          {
            model: "heading5",
            view: "h5",
            title: "Heading 5",
            class: "ck-heading_heading5",
          },
          {
            model: "heading6",
            view: "h6",
            title: "Heading 6",
            class: "ck-heading_heading6",
          },
        ],
      },
      htmlSupport: {
        allow: [
          {
            name: /^.*$/,
            styles: true,
            attributes: true,
            classes: true,
          },
        ],
      },
      image: {
        toolbar: [
          "toggleImageCaption",
          "imageTextAlternative",
          "|",
          "imageStyle:inline",
          "imageStyle:wrapText",
          "imageStyle:breakText",
          "|",
          "resizeImage",
        ],
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: "https://",
        decorators: {
          toggleDownloadable: {
            mode: "manual",
            label: "Downloadable",
            attributes: {
              download: "file",
            },
          },
        },
      },
      list: {
        properties: {
          styles: true,
          startIndex: true,
          reversed: true,
        },
      },
      placeholder: "Type or paste your content here!",
      table: {
        contentToolbar: [
          "tableColumn",
          "tableRow",
          "mergeTableCells",
          "tableProperties",
          "tableCellProperties",
        ],
      },
    } as EditorConfig;

    this.isLayoutReady = true;
  }
}
